import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getData as getCountryList } from "country-list";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PhoneInput from "react-phone-input-2";

import CommonStyles from "../../common/CommonStyles";
import InputBox from "../../common2/InputBox";
import CustomInput from "../../common/textField/CustomInput";
import CustomTheme from "../../common/CustomTheme";

import { IState } from "../../../redux/reducers/rootReducers";
import {
  createAzureUser,
  updateAzureUser,
  getAzureUserDetails,
  getAzureGroups,
  getAzureDomains,
  getSubscribedSkus,
  addUserToGroup,
  removeUserFromGroup,
  assignLicense,
} from "../../../redux/actions/userManagement/azureUsers";
import CustomFormLabel from "../../common/form-label/CustomFormLabel ";

type Props = {
  isEdit: boolean;
  userId: string | null;
  style?: any;
};

const COMMON_WEAK_PASSWORDS = new Set([
  "password",
  "password1",
  "password123",
  "admin",
  "admin123",
  "welcome",
  "welcome123",
  "qwerty",
  "qwerty123",
  "letmein",
  "iloveyou",
  "12345678",
  "123456789",
  "1234567890",
  "abc123",
  "company123",
  "test1234",
]);

function normalizeForCompare(value: string) {
  return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function splitIdentityParts(value: string) {
  if (!value) return [];
  return value
    .split(/[\s._@-]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function sanitizeMailNickname(value: string) {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9._-]/g, "");
}

function passwordRules(pw: string) {
  const v = pw || "";
  const minLen = v.length >= 8;

  const hasLower = /[a-z]/.test(v);
  const hasUpper = /[A-Z]/.test(v);
  const hasNumber = /[0-9]/.test(v);
  const hasSymbol = /[^A-Za-z0-9]/.test(v);

  const categories = [hasLower, hasUpper, hasNumber, hasSymbol].filter(Boolean).length;
  const has3of4 = categories >= 3;

  const lower = v.toLowerCase();
  const isCommon = COMMON_WEAK_PASSWORDS.has(lower);

  const allSameChar = v.length > 0 && v.split("").every((c) => c === v[0]);
  const ascendingAlpha = "abcdefghijklmnopqrstuvwxyz".includes(lower);
  const ascendingNum = "0123456789".includes(v);
  const qwertySeq = "qwertyuiop".includes(lower);
  const containsPasswordWord = lower.includes("password");

  const notWeak = !(
    isCommon ||
    allSameChar ||
    ascendingAlpha ||
    ascendingNum ||
    qwertySeq ||
    containsPasswordWord
  );

  return {
    minLen,
    hasLower,
    hasUpper,
    hasNumber,
    hasSymbol,
    has3of4,
    notWeak,
    ok: minLen && has3of4 && notWeak,
  };
}

function getPasswordError(pw: string) {
  const r = passwordRules(pw);
  if (!pw) return "Password is Required";
  if (!r.minLen) return "Password must be at least 8 characters.";
  if (!r.has3of4) {
    return "Password must include at least 3 of: lowercase, uppercase, number, symbol.";
  }
  if (!r.notWeak) return "Password is too weak or commonly used.";
  return "";
}

function getPasswordIdentityError(params: {
  password: string;
  userPrincipalName: string;
  mailNickname: string;
  displayName: string;
  givenName: string;
  surname: string;
}) {
  const {
    password,
    userPrincipalName,
    mailNickname,
    displayName,
    givenName,
    surname,
  } = params;

  const normalizedPassword = normalizeForCompare(password);
  if (!normalizedPassword) return "";

  const upnLocalPart = (userPrincipalName || "").split("@")[0] || "";

  const rawTokens = [
    upnLocalPart,
    mailNickname,
    displayName,
    givenName,
    surname,
    `${givenName}${surname}`,
    `${surname}${givenName}`,
    ...splitIdentityParts(upnLocalPart),
    ...splitIdentityParts(mailNickname),
    ...splitIdentityParts(displayName),
    ...splitIdentityParts(givenName),
    ...splitIdentityParts(surname),
  ];

  const tokens = Array.from(
    new Set(rawTokens.map((x) => normalizeForCompare(x)).filter((x) => x.length >= 3))
  );

  for (const token of tokens) {
    if (normalizedPassword.includes(token)) {
      return "Password cannot contain the username, email prefix, or parts of the user's name.";
    }
  }

  return "";
}

function extractApiErrorMessage(error: any) {
  const errObj =
    error?.response?.data ||
    error?.payload?.response?.data ||
    error?.payload ||
    error;

  return (
    errObj?.error?.details?.[0]?.message ||
    errObj?.error?.message ||
    errObj?.message ||
    "Something went wrong while saving the Azure user."
  );
}

function ReadonlyValueBox({
  title,
  value,
  classes,
  fieldBg,
  fieldText,
  fieldBorder,
}: any) {
  const displayValue =
    value === null || value === undefined || value === "" ? "------" : String(value);

  return (
    <Grid item md={4} xs={12}>
      <Box
        sx={{
          paddingLeft: "20px",
          paddingRight: "20px",
          paddingTop: "8px",
          paddingBottom: "4px",
        }}
      >
        <Typography
          className={classes.BoxTitle}
          sx={{
            whiteSpace: "nowrap",
            lineHeight: "20px",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          marginLeft: "20px",
          marginRight: "20px",
          minHeight: "40px",
          borderRadius: "4px",
          border: `1px solid ${fieldBorder}`,
          backgroundColor: fieldBg,
          color: fieldText,
          display: "flex",
          alignItems: "center",
          padding: "0 14px",
          overflow: "hidden",
        }}
      >
        <Tooltip title={displayValue} arrow placement="top-start">
          <Typography
            variant="body2"
            sx={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayValue}
          </Typography>
        </Tooltip>
      </Box>
    </Grid>
  );
}

const AzureUserFormPage = ({ isEdit, userId }: Props) => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const classes = CommonStyles();

  const detailsState = useSelector((state: IState) => state?.azureUserReducer?.UserDetails);
  const groupsState = useSelector((state: IState) => state?.azureUserReducer?.Groups);
  const skusState = useSelector((state: IState) => state?.azureUserReducer?.Skus);
  const domainsState = useSelector((state: IState) => state?.azureUserReducer?.Domains);
  const toggleTheme = useSelector((state: IState) => state.theme.theme);

  const loadingDetails = detailsState?.loading || false;
  const detailsData = detailsState?.data || null;
  const details = detailsData?.user || null;
  const memberOf = detailsData?.memberOf || [];
  const licenseDetails = detailsData?.licenseDetails || [];

  const themeData = CustomTheme();
  const mode = toggleTheme ? themeData.all.dark : themeData.all.light;
  const isDark = !!toggleTheme;

  const fieldBg = isDark ? mode?.body || "#2a2d35" : "#ffffff";
  const fieldText = isDark ? mode?.text || "#ffffff" : "#000000";
  const fieldBorder = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.16)";
  const helperText = isDark ? "#d1d5db" : "#6b7280";

  const commonFieldShell = {
    height: "40px",
    width: "95%",
    backgroundColor: fieldBg,
    color: fieldText,
    border: `1px solid ${fieldBorder}`,
    borderRadius: "4px",
  };

  const [activeStep, setActiveStep] = useState(0);
  const [deriveMailNickname, setDeriveMailNickname] = useState(!isEdit);
  // password visibility toggle — Basics tab only
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<any>({
    displayName: "",
    userPrincipalName: "",
    upnLocalPart: "",
    upnDomain: "",
    mailNickname: "",
    password: "",
    givenName: "",
    surname: "",
    jobTitle: "",
    mobilePhone: "",
    officeLocation: "",
    usageLocation: "",
    alternateEmail: "",
    accountEnabled: true,
  });

  const [showErr, setShowErr] = useState<any>({
    displayName: false,
    userPrincipalName: false,
    mailNickname: false,
    password: false,
  });

  const [passwordErrMsg, setPasswordErrMsg] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedSkuIds, setSelectedSkuIds] = useState<string[]>([]);

  useEffect(() => {
    dispatch(getAzureGroups({ pageSize: 200 }));
    dispatch(getSubscribedSkus());
    dispatch(getAzureDomains());

    if (isEdit && userId) {
      dispatch(getAzureUserDetails(userId));
    }
  }, [dispatch, isEdit, userId]);

  const domains = useMemo(() => {
    const d = domainsState?.data;
    return Array.isArray(d) ? d : [];
  }, [domainsState]);

  const domainsLoading = domainsState?.loading || false;

  const defaultDomain = useMemo(() => {
    const preferred =
      domains.find((d: any) => d?.isDefault) ||
      domains.find((d: any) => d?.isInitial) ||
      domains[0];

    return preferred?.id || "";
  }, [domains]);

  useEffect(() => {
    if (!isEdit && defaultDomain && !formData.upnDomain) {
      setFormData((prev: any) => ({
        ...prev,
        upnDomain: defaultDomain,
      }));
    }
  }, [defaultDomain, isEdit, formData.upnDomain]);

  useEffect(() => {
    if (!isEdit || !details || !userId) return;
    if (String(details?.id || "").toLowerCase() !== String(userId).toLowerCase()) return;

    const upn = details?.userPrincipalName || details?.mail || details?.email || "";
    const [localPart, domainPart] = upn.includes("@") ? upn.split("@") : [upn, ""];

    setFormData({
      displayName: details?.displayName || details?.name || "",
      userPrincipalName: upn,
      upnLocalPart: localPart || "",
      upnDomain: domainPart || defaultDomain || "",
      mailNickname: details?.mailNickname || details?.userName || "",
      password: "",
      givenName: details?.givenName || "",
      surname: details?.surname || "",
      jobTitle: details?.jobTitle || "",
      mobilePhone: details?.mobilePhone || "",
      officeLocation: details?.officeLocation || "",
      usageLocation: details?.usageLocation || "",
      alternateEmail: details?.otherMails?.[0] || "",
      accountEnabled:
        typeof details?.accountEnabled === "boolean" ? details.accountEnabled : true,
    });

    setSelectedGroupIds((memberOf || []).map((x: any) => String(x?.id)).filter(Boolean));
    setSelectedSkuIds(
      (licenseDetails || [])
        .map((x: any) => String(x?.skuId || x?.skuID || x?.id || ""))
        .filter(Boolean)
    );
    setDeriveMailNickname(false);
  }, [isEdit, details, userId, memberOf, licenseDetails, defaultDomain]);

  const groups = useMemo(() => {
    const g = groupsState?.data;
    if (!g) return [];
    if (Array.isArray(g)) return g;
    if (Array.isArray(g?.value)) return g.value;
    if (Array.isArray(g?.data)) return g.data;
    return [];
  }, [groupsState]);

  const skus = useMemo(() => {
    const d = skusState?.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.value)) return d.value;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [skusState]);

  const usageLocationOptions = useMemo(() => {
    return (getCountryList() as { name: string; code: string }[])
      .map((c) => ({
        label: c.name,
        value: c.code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const selectedUsageLocation = useMemo(() => {
    return (
      usageLocationOptions.find(
        (x) => String(x.value).toUpperCase() === String(formData.usageLocation || "").toUpperCase()
      ) || null
    );
  }, [usageLocationOptions, formData.usageLocation]);

  const selectedGroups = useMemo(
    () => groups.filter((g: any) => selectedGroupIds.includes(String(g?.id))),
    [groups, selectedGroupIds]
  );

  const selectedSkus = useMemo(
    () =>
      skus.filter((s: any) =>
        selectedSkuIds.includes(String(s?.skuId || s?.skuID || s?.id || ""))
      ),
    [skus, selectedSkuIds]
  );

  const originalGroupIds = useMemo(
    () => (memberOf || []).map((x: any) => String(x?.id)).filter(Boolean),
    [memberOf]
  );

  const originalSkuIds = useMemo(
    () =>
      (licenseDetails || [])
        .map((x: any) => String(x?.skuId || x?.skuID || x?.id || ""))
        .filter(Boolean),
    [licenseDetails]
  );

  const effectiveUserPrincipalName = useMemo(() => {
    if (isEdit) return formData.userPrincipalName?.trim() || "";

    const localPart = formData.upnLocalPart?.trim() || "";
    const domain = formData.upnDomain?.trim() || "";

    if (localPart && domain) return `${localPart}@${domain}`;
    return "";
  }, [isEdit, formData]);

  const currentUserMatchesRoute =
    !isEdit ||
    !userId ||
    (details?.id && String(details.id).toLowerCase() === String(userId).toLowerCase());

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    setShowErr((prev: any) => ({
      ...prev,
      [name]: false,
    }));

    if (name === "password") {
      setPasswordErrMsg("");
    }

    if (submitError) {
      setSubmitError("");
    }
  };

  const handleUpnLocalPartChange = (e: any) => {
    const value = e.target.value;

    setFormData((prev: any) => ({
      ...prev,
      upnLocalPart: value,
      mailNickname: deriveMailNickname ? sanitizeMailNickname(value) : prev.mailNickname,
    }));

    setShowErr((prev: any) => ({
      ...prev,
      userPrincipalName: false,
    }));

    if (submitError) {
      setSubmitError("");
    }
  };

  const handleMailNicknameChange = (e: any) => {
    const value = e.target.value;

    setFormData((prev: any) => ({
      ...prev,
      mailNickname: value,
    }));

    setShowErr((prev: any) => ({
      ...prev,
      mailNickname: false,
    }));

    if (submitError) {
      setSubmitError("");
    }
  };

  const handleDeriveMailNickname = (checked: boolean) => {
    setDeriveMailNickname(checked);

    if (checked) {
      setFormData((prev: any) => ({
        ...prev,
        mailNickname: sanitizeMailNickname(prev.upnLocalPart),
      }));
    }
  };

  const handlePasswordChange = (e: any) => {
    const value = e.target.value;

    setFormData((prev: any) => ({
      ...prev,
      password: value,
    }));

    setShowErr((prev: any) => ({
      ...prev,
      password: false,
    }));

    setPasswordErrMsg("");
    if (submitError) {
      setSubmitError("");
    }
  };

  // Silent validation — checks required fields without touching showErr state.
  // Returns list of tab names that have errors.
  const silentValidate = () => {
    const failingTabs: string[] = [];

    // Basics checks
    const basicsInvalid =
      !formData.displayName?.trim() ||
      (!isEdit && (
        !formData.upnLocalPart?.trim() ||
        !formData.upnDomain?.trim() ||
        !formData.mailNickname?.trim() ||
        !!getPasswordError(formData.password) ||
        !!getPasswordIdentityError({
          password: formData.password,
          userPrincipalName: effectiveUserPrincipalName,
          mailNickname: formData.mailNickname,
          displayName: formData.displayName,
          givenName: formData.givenName,
          surname: formData.surname,
        })
      ));

    if (basicsInvalid) failingTabs.push("Basics");

    // Properties checks
    if (formData.usageLocation?.trim() && formData.usageLocation.trim().length !== 2) {
      failingTabs.push("Properties");
    }

    return failingTabs;
  };

  // Auto-validate when user lands on Review tab
  useEffect(() => {
    if (activeStep !== 3) {
      setReviewError("");
      return;
    }
    const failingTabs = silentValidate();
    if (failingTabs.length > 0) {
      setReviewError(
        `Validation failed. Required information is missing or not valid for the following tab${failingTabs.length > 1 ? "s" : ""}: ${failingTabs.join(", ")}.`
      );
    } else {
      setReviewError("");
    }
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateBasics = () => {
    const nextErr = {
      displayName: false,
      userPrincipalName: false,
      mailNickname: false,
      password: false,
    };

    let valid = true;

    if (!formData.displayName?.trim()) {
      nextErr.displayName = true;
      valid = false;
    }

    if (!isEdit) {
      if (!formData.upnLocalPart?.trim() || !formData.upnDomain?.trim()) {
        nextErr.userPrincipalName = true;
        valid = false;
      }

      if (!formData.mailNickname?.trim()) {
        nextErr.mailNickname = true;
        valid = false;
      }

      const pwdErr = getPasswordError(formData.password);
      const identityErr = getPasswordIdentityError({
        password: formData.password,
        userPrincipalName: effectiveUserPrincipalName,
        mailNickname: formData.mailNickname,
        displayName: formData.displayName,
        givenName: formData.givenName,
        surname: formData.surname,
      });

      const finalPwdErr = pwdErr || identityErr;

      if (finalPwdErr) {
        nextErr.password = true;
        setPasswordErrMsg(finalPwdErr);
        valid = false;
      } else {
        setPasswordErrMsg("");
      }
    }

    setShowErr(nextErr);
    return valid;
  };

  const validateProperties = () => {
    if (formData.usageLocation?.trim() && formData.usageLocation.trim().length !== 2) {
      setSubmitError("Usage Location must be a 2-letter code like IN, US, or GB.");
      return false;
    }

    setSubmitError("");
    return true;
  };

  const backHandler = () => {
    navigate("/AzureManagement/AzureUsers");
  };

  const nextStep = () => {
    if (activeStep === 0) {
      const ok = validateBasics();
      if (!ok) return;
    }

    if (activeStep === 1) {
      const ok = validateProperties();
      if (!ok) return;
    }

    setActiveStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const createUserAsync = (payload: any) =>
    new Promise<any>((resolve, reject) => {
      dispatch(
        createAzureUser(payload, (status: "success" | "failure", data: any) => {
          if (status === "success") resolve(data);
          else reject(data);
        })
      );
    });

  const updateUserAsync = (targetUserId: string, payload: any) =>
    new Promise<any>((resolve, reject) => {
      dispatch(
        updateAzureUser(targetUserId, payload, (status: "success" | "failure", data: any) => {
          if (status === "success") resolve(data);
          else reject(data);
        })
      );
    });

  const addUserToGroupAsync = (groupId: string, targetUserId: string) =>
    new Promise<any>((resolve, reject) => {
      dispatch(
        addUserToGroup(groupId, targetUserId, (status: "success" | "failure", data: any) => {
          if (status === "success") resolve(data);
          else reject(data);
        })
      );
    });

  const removeUserFromGroupAsync = (groupId: string, targetUserId: string) =>
    new Promise<any>((resolve, reject) => {
      dispatch(
        removeUserFromGroup(groupId, targetUserId, (status: "success" | "failure", data: any) => {
          if (status === "success") resolve(data);
          else reject(data);
        })
      );
    });

  const assignLicenseAsync = (targetUserId: string, payload: any) =>
    new Promise<any>((resolve, reject) => {
      dispatch(
        assignLicense(targetUserId, payload, (status: "success" | "failure", data: any) => {
          if (status === "success") resolve(data);
          else reject(data);
        })
      );
    });

  const syncAssignments = async (targetUserId: string) => {
    const currentGroupSet = new Set<string>(selectedGroupIds);
    const originalGroupSet = new Set<string>(originalGroupIds);

    const groupsToAdd: string[] = Array.from(currentGroupSet).filter((id) => !originalGroupSet.has(id));
    const groupsToRemove: string[] = isEdit
      ? Array.from(originalGroupSet).filter((id) => !currentGroupSet.has(id))
      : [];

    for (const gid of groupsToAdd) {
      await addUserToGroupAsync(gid, targetUserId);
    }

    for (const gid of groupsToRemove) {
      await removeUserFromGroupAsync(gid, targetUserId);
    }

    const currentSkuSet = new Set<string>(selectedSkuIds);
    const originalSkuSet = new Set<string>(isEdit ? originalSkuIds : []);

    const addSkuIds: string[] = Array.from(currentSkuSet).filter((id) => !originalSkuSet.has(id));
    const removeSkuIds: string[] = Array.from(originalSkuSet).filter((id) => !currentSkuSet.has(id));

    if (addSkuIds.length > 0 || removeSkuIds.length > 0) {
      await assignLicenseAsync(targetUserId, {
        addSkuIds,
        removeSkuIds,
      });
    }
  };

  const submitHandler = async () => {
    if (isSubmitting) return;

    setSubmitError("");

    const basicValid = validateBasics();
    const propertiesValid = validateProperties();

    if (!basicValid || !propertiesValid) {
      if (activeStep < 1) setActiveStep(0);
      return;
    }

    setIsSubmitting(true);

    const payload: any = {
      displayName: formData.displayName?.trim(),
      givenName: formData.givenName?.trim() || null,
      surname: formData.surname?.trim() || null,
      jobTitle: formData.jobTitle?.trim() || null,
      mobilePhone: formData.mobilePhone?.trim() || null,
      officeLocation: formData.officeLocation?.trim() || null,
      usageLocation: formData.usageLocation?.trim()?.toUpperCase() || null,
      accountEnabled: !!formData.accountEnabled,
    };

    if (formData.alternateEmail?.trim()) {
      payload.otherMails = [formData.alternateEmail.trim()];
    }

    if (!isEdit) {
      const finalMailNickname =
        formData.mailNickname?.trim() || sanitizeMailNickname(formData.upnLocalPart);

      payload.userPrincipalName = effectiveUserPrincipalName?.trim();
      payload.password = formData.password;
      payload.mailNickname = finalMailNickname || null;
      payload.forceChangePasswordNextSignIn = true;
    }

    try {
      let targetUserId = userId || "";

      if (isEdit && userId) {
        await updateUserAsync(userId, payload);
      } else {
        const createdUser = await createUserAsync(payload);
        targetUserId = createdUser?.id || createdUser?.user?.id || "";

        if (!targetUserId) {
          navigate("/AzureManagement/AzureUsers");
          return;
        }
      }

      if (targetUserId) {
        await syncAssignments(targetUserId);
      }

      navigate("/AzureManagement/AzureUsers");
    } catch (error: any) {
      setSubmitError(extractApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const reviewGroupNames = selectedGroups
    .map((g: any) => g?.displayName || g?.name || "")
    .filter(Boolean)
    .join(", ");

  const reviewLicenseNames = selectedSkus
    .map((s: any) => s?.skuPartNumber || s?.partNumber || s?.displayName || "")
    .filter(Boolean)
    .join(", ");

  if (isEdit && (!currentUserMatchesRoute || (loadingDetails && !details))) {
    return (
      <Box className={`pageHolder ${classes.px1}`}>
        <Box
          sx={{
            minHeight: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={`pageHolder ${classes.px1}`}
      sx={{
        "& .h-inputs input": {
          backgroundColor: `${fieldBg} !important`,
          color: `${fieldText} !important`,
        },
        "& .h-inputs .MuiInputBase-input": {
          backgroundColor: `${fieldBg} !important`,
          color: `${fieldText} !important`,
        },
        "& .h-inputs .MuiOutlinedInput-root": {
          backgroundColor: `${fieldBg} !important`,
        },
        "& .react-tel-input .form-control": {
          height: "40px !important",
          width: "95% !important",
          backgroundColor: `${fieldBg} !important`,
          color: `${fieldText} !important`,
          border: `1px solid ${fieldBorder} !important`,
        },
        "& .react-tel-input .flag-dropdown": {
          backgroundColor: `${fieldBg} !important`,
          border: `1px solid ${fieldBorder} !important`,
        },
        "& .react-tel-input .selected-flag": {
          backgroundColor: `${fieldBg} !important`,
        },
        "& .react-tel-input .country-list": {
          backgroundColor: `${fieldBg} !important`,
          color: `${fieldText} !important`,
        },
        "& .react-tel-input .country-list .country": {
          color: `${fieldText} !important`,
        },
        "& .react-tel-input .country-list .country:hover": {
          backgroundColor: isDark ? "#3a3f4b !important" : "#f3f4f6 !important",
        },
        "& .azure-helper-text": {
          color: `${helperText} !important`,
          fontSize: "12px",
          marginTop: "6px",
          paddingLeft: "20px",
        },
      }}
    >
      <Box>
        <Box className="mb2 pb2">
          <Typography variant="h6" className={`${classes.themeColorTxt}`}>
            {isEdit ? "Edit Azure User" : "Add Azure User"}
          </Typography>
        </Box>

        {submitError ? (
          <Box className="mb1 px-25">
            <Alert severity="error">{submitError}</Alert>
          </Box>
        ) : null}

        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
          }}
        >
          <Tabs
            value={activeStep}
            onChange={(_, v) => setActiveStep(v)}
          >
            <Tab label="Basics" />
            <Tab label="Properties" />
            <Tab label="Assignments" />
            <Tab label={isEdit ? "Review + Update" : "Review + Create"} />
          </Tabs>
        </Box>

        {/* ── BASICS TAB ONLY: single left-aligned column, same width as original md=4 ── */}
        {activeStep === 0 && (
          <Grid container>
            <Grid item xs={12} md={4}>
              <Grid container spacing={1} direction="column">

                <Grid item xs={12}>
                  <InputBox
                    title="Name "
                    type="text"
                    placeholder="Please Enter"
                    styleProp={classes.BoxTitle}
                    inputStyle={classes.inputStyle}
                    name="displayName"
                    required={true}
                    onChange={handleChange}
                    value={formData.displayName}
                    showErr={showErr.displayName}
                    errMsg={"Display Name is Required"}
                    classname="h-inputs"
                  />
                </Grid>

                {!isEdit ? (
                  <Grid item xs={12}>
                    <Box sx={{ paddingLeft: "20px", paddingBottom: "4px" }}>
                      <CustomFormLabel labelName={"User Principal Name"} isMandotary={true} />
                    </Box>

                    <Box
                      sx={{
                        marginLeft: "20px",
                        marginRight: "20px",
                        display: "flex",
                        alignItems: "stretch",
                        width: "calc(100% - 40px)",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          border: `1px solid ${showErr.userPrincipalName ? "#d32f2f" : fieldBorder}`,
                          borderRadius: "4px",
                          backgroundColor: fieldBg,
                          display: "flex",
                          alignItems: "center",
                          padding: "0 14px",
                        }}
                      >
                        <input
                          type="text"
                          name="upnLocalPart"
                          value={formData.upnLocalPart}
                          onChange={handleUpnLocalPartChange}
                          style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            color: fieldText,
                            fontSize: "16px",
                          }}
                          placeholder="Please Enter"
                        />
                      </Box>

                      <Select
                        value={formData.upnDomain}
                        onChange={(e) =>
                          setFormData((prev: any) => ({
                            ...prev,
                            upnDomain: e.target.value,
                          }))
                        }
                        disabled={domainsLoading || domains.length === 0}
                        displayEmpty
                        sx={{
                          minWidth: 160,
                          height: "40px",
                          backgroundColor: fieldBg,
                          color: fieldText,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: fieldBorder,
                          },
                          "& .MuiSvgIcon-root": {
                            color: fieldText,
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: fieldBg,
                              color: fieldText,
                              "& .MuiMenuItem-root:hover": {
                                backgroundColor: isDark ? "#3a3f4b" : "#f3f4f6",
                              },
                              "& .MuiMenuItem-root.Mui-selected": {
                                backgroundColor: isDark ? "#4a4f5b" : "#e8eaf6",
                              },
                              "& .MuiMenuItem-root.Mui-selected:hover": {
                                backgroundColor: isDark ? "#4a4f5b" : "#e8eaf6",
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          {domainsLoading ? "Loading domains..." : "Select domain"}
                        </MenuItem>
                        {domains.map((d: any) => (
                          <MenuItem key={d.id} value={d.id}>
                            @{d.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {showErr.userPrincipalName ? (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "12px", mt: 0.5, display: "block", pl: "20px" }}
                      >
                        User Principal Name is Required
                      </Typography>
                    ) : (
                      <Typography className="azure-helper-text">
                        Domains are loaded from Microsoft Graph verified tenant domains.
                      </Typography>
                    )}
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ paddingLeft: "20px", paddingBottom: "4px" }}>
                      <Typography className={classes.BoxTitle} sx={{ whiteSpace: "nowrap", lineHeight: "20px" }}>
                        User Principal Name
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        marginLeft: "20px",
                        marginRight: "20px",
                        minHeight: "40px",
                        borderRadius: "4px",
                        border: `1px solid ${fieldBorder}`,
                        backgroundColor: fieldBg,
                        color: fieldText,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 14px",
                        overflow: "hidden",
                      }}
                    >
                      <Tooltip title={formData.userPrincipalName || "------"} arrow placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {formData.userPrincipalName || "------"}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}

                {!isEdit && (
                  <Grid item xs={12}>
                    <InputBox
                      title="Mail Nickname"
                      type="text"
                      placeholder="Please Enter"
                      styleProp={classes.BoxTitle}
                      inputStyle={classes.inputStyle}
                      name="mailNickname"
                      onChange={handleMailNicknameChange}
                      value={formData.mailNickname}
                      classname="h-inputs"
                      disabled={deriveMailNickname}
                      required={true}
                      showErr={showErr.mailNickname}
                      errMsg={"Mail Nickname is Required"}
                    />

                    <Box sx={{ pl: "20px", pt: "4px" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={deriveMailNickname}
                            onChange={(e) => handleDeriveMailNickname(e.target.checked)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Derive from User Principal Name
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}

                {!isEdit && (
                  <Grid item xs={12}>
                    <Box sx={{ paddingLeft: "20px" }}>
                      <CustomFormLabel labelName={"Password"} isMandotary={true} />
                    </Box>

                    <Box sx={{ position: "relative" }}>
                      <CustomInput
                        type={showPassword ? "text" : "password"}
                        name="password"
                        label="Enter the password"
                        placeholder="Please Enter"
                        value={formData.password}
                        onChange={handlePasswordChange}
                        isError={showErr.password}
                        helperText={showErr.password ? passwordErrMsg : ""}
                        classNames="h-inputs"
                        readOnly={false}
                        isDisabled={false}
                        autocomplete="new-password"
                        iconPosition="start"
                        icon={
                          <Tooltip
                            title={
                              <div style={{ fontSize: "12px" }} className="password-policy">
                                <Typography
                                  variant="subtitle2"
                                  sx={{ mb: 1, fontSize: "14px !important" }}
                                >
                                  Password Policy :
                                </Typography>
                                <ul
                                  style={{
                                    paddingLeft: "20px",
                                    margin: "8px 0",
                                    fontSize: "12px !important",
                                  }}
                                >
                                  <li>Minimum 8 characters.</li>
                                  <li>At least 3 of 4: lowercase, uppercase, number, symbol.</li>
                                  <li>Must not be weak or commonly used.</li>
                                  <li>Must not contain username, email prefix, or name parts.</li>
                                </ul>
                              </div>
                            }
                            arrow
                            placement="top-end"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: document.body.classList.contains("dark-mode")
                                    ? "#3f4044"
                                    : "#ffffff",
                                  color: document.body.classList.contains("dark-mode")
                                    ? "#ffffff"
                                    : "#000000",
                                  fontSize: "14px",
                                },
                              },
                            }}
                            PopperProps={{
                              modifiers: [
                                {
                                  name: "offset",
                                  options: {
                                    offset: [-40, 5],
                                  },
                                },
                              ],
                            }}
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 16, color: "gray", cursor: "pointer" }}
                            />
                          </Tooltip>
                        }
                      />

                      {/* Eye icon — toggles password visibility */}
                      <Box
                        onClick={() => setShowPassword((prev) => !prev)}
                        sx={{
                          position: "absolute",
                          top: showErr.password ? "30px" : "50%",
                          right: "24px",
                          transform: showErr.password ? "none" : "translateY(-50%)",
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          color: "gray",
                          zIndex: 1,
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ minHeight: "18px" }} />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ paddingLeft: "20px", paddingTop: "6px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formData.accountEnabled}
                          onChange={(e) =>
                            setFormData((prev: any) => ({
                              ...prev,
                              accountEnabled: e.target.checked,
                            }))
                          }
                        />
                      }
                      label="Account Enabled"
                    />
                  </Box>
                </Grid>

              </Grid>
            </Grid>
          </Grid>
        )}

        {/* ── ALL OTHER TABS: exactly as original ── */}

        {activeStep === 1 && (
          <Grid container spacing={1}>
            <Grid item md={4} xs={12}>
              <InputBox
                title="First Name"
                type="text"
                placeholder="Please Enter"
                styleProp={classes.BoxTitle}
                inputStyle={classes.inputStyle}
                name="givenName"
                onChange={handleChange}
                value={formData.givenName}
                classname="h-inputs"
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <InputBox
                title="Last Name"
                type="text"
                placeholder="Please Enter"
                styleProp={classes.BoxTitle}
                inputStyle={classes.inputStyle}
                name="surname"
                onChange={handleChange}
                value={formData.surname}
                classname="h-inputs"
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <InputBox
                title="Job Title"
                type="text"
                placeholder="Please Enter"
                styleProp={classes.BoxTitle}
                inputStyle={classes.inputStyle}
                name="jobTitle"
                onChange={handleChange}
                value={formData.jobTitle}
                classname="h-inputs"
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <Box sx={{ paddingLeft: "20px", width: "calc(100% - 40px)" }}>
                <CustomFormLabel labelName={"Mobile No"} isMandotary={false} />
              </Box>

              <Box sx={{ paddingLeft: "20px" }}>
                <PhoneInput
                  country={"gb"}
                  value={formData.mobilePhone}
                  onChange={(e: any) => {
                    setFormData((prev: any) => ({
                      ...prev,
                      mobilePhone: e,
                    }));
                  }}
                  defaultErrorMessage="Something happened"
                  inputProps={{
                    name: "mobilePhone",
                    required: false,
                  }}
                  inputStyle={commonFieldShell}
                  buttonStyle={{
                    backgroundColor: fieldBg,
                    border: `1px solid ${fieldBorder}`,
                    borderRadius: "4px 0 0 4px",
                  }}
                  dropdownStyle={{
                    backgroundColor: fieldBg,
                    color: fieldText,
                  }}
                />
              </Box>
            </Grid>

            <Grid item md={4} xs={12}>
              <InputBox
                title="Office Location"
                type="text"
                placeholder="Please Enter"
                styleProp={classes.BoxTitle}
                inputStyle={classes.inputStyle}
                name="officeLocation"
                onChange={handleChange}
                value={formData.officeLocation}
                classname="h-inputs"
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <InputBox
                title="Alternate Email"
                type="text"
                placeholder="Please Enter"
                styleProp={classes.BoxTitle}
                inputStyle={classes.inputStyle}
                name="alternateEmail"
                onChange={handleChange}
                value={formData.alternateEmail}
                classname="h-inputs"
              />
            </Grid>

            <Grid item md={4} xs={12}>
              <Box sx={{ paddingLeft: "20px", paddingBottom: "4px" }}>
                <CustomFormLabel labelName={"Usage Location"} isMandotary={false} />
              </Box>

              <Box sx={{ marginLeft: "20px", marginRight: "20px" }}>
                <Autocomplete
                  options={usageLocationOptions}
                  value={selectedUsageLocation}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  componentsProps={{
                    popper: {
                      placement: "bottom-start",
                      modifiers: [{ name: "flip", enabled: false }],
                    },
                  }}
                  ListboxProps={{ style: { maxHeight: "35vh" } }}
                  onChange={(_, value) => {
                    setFormData((prev: any) => ({
                      ...prev,
                      usageLocation: value?.value || "",
                    }));
                    setSubmitError("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select usage location"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: fieldBg,
                          color: fieldText,
                          minHeight: "40px",
                        },
                        "& .MuiOutlinedInput-input": {
                          color: fieldText,
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: fieldBg,
                      color: fieldText,
                    },
                  }}
                />
              </Box>

              <Typography className="azure-helper-text">
                Stored as ISO code, shown as country name.
              </Typography>
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Box sx={{ paddingLeft: "20px", paddingBottom: "6px" }}>
                <Typography className={classes.BoxTitle}>Groups</Typography>
              </Box>

              <Box sx={{ marginLeft: "20px", marginRight: "20px" }}>
                <Autocomplete
                  multiple
                  options={groups}
                  disableCloseOnSelect
                  value={selectedGroups}
                  getOptionLabel={(option: any) =>
                    option?.displayName || option?.name || option?.id || ""
                  }
                  onChange={(_, value: any[]) => {
                    setSelectedGroupIds(value.map((x) => String(x?.id)).filter(Boolean));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select groups" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: fieldBg,
                      color: fieldText,
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item md={6} xs={12}>
              <Box sx={{ paddingLeft: "20px", paddingBottom: "6px" }}>
                <Typography className={classes.BoxTitle}>Licenses</Typography>
              </Box>

              <Box sx={{ marginLeft: "20px", marginRight: "20px" }}>
                <Autocomplete
                  multiple
                  options={skus}
                  disableCloseOnSelect
                  value={selectedSkus}
                  getOptionLabel={(option: any) =>
                    option?.skuPartNumber ||
                    option?.partNumber ||
                    option?.displayName ||
                    option?.skuId ||
                    ""
                  }
                  onChange={(_, value: any[]) => {
                    setSelectedSkuIds(
                      value
                        .map((x) => String(x?.skuId || x?.skuID || x?.id || ""))
                        .filter(Boolean)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select licenses" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: fieldBg,
                      color: fieldText,
                    },
                  }}
                />
              </Box>
            </Grid>

            {!isEdit && (
              <Grid item xs={12}>
                <Box sx={{ marginLeft: "20px", marginRight: "20px" }}>
                  <Alert severity="info">
                    Assignments will be applied immediately after the user is created.
                  </Alert>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {activeStep === 3 && (
          <Grid container spacing={1}>
            {reviewError && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {reviewError}
                </Alert>
              </Grid>
            )}
            <ReadonlyValueBox
              title="Name"
              value={formData.displayName}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="User Principal Name"
              value={effectiveUserPrincipalName}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Mail Nickname"
              value={formData.mailNickname}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            {!isEdit && (
              <ReadonlyValueBox
                title="Password"
                value={formData.password ? "********" : "------"}
                classes={classes}
                fieldBg={fieldBg}
                fieldText={fieldText}
                fieldBorder={fieldBorder}
              />
            )}

            <ReadonlyValueBox
              title="Account Enabled"
              value={formData.accountEnabled ? "Yes" : "No"}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="First Name"
              value={formData.givenName}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Last Name"
              value={formData.surname}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Job Title"
              value={formData.jobTitle}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Mobile No"
              value={formData.mobilePhone}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Office Location"
              value={formData.officeLocation}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Usage Location"
              value={selectedUsageLocation?.label || formData.usageLocation}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Alternate Email"
              value={formData.alternateEmail}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Groups"
              value={reviewGroupNames || "------"}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />

            <ReadonlyValueBox
              title="Licenses"
              value={reviewLicenseNames || "------"}
              classes={classes}
              fieldBg={fieldBg}
              fieldText={fieldText}
              fieldBorder={fieldBorder}
            />
          </Grid>
        )}

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box
              className="text-center"
              sx={{ marginTop: "16px", display: "flex", justifyContent: "center", gap: 2 }}
            >
              <Button
                className={`${classes.btn} ${classes.btnCancel}`}
                variant="outlined"
                onClick={backHandler}
              >
                <CancelIcon style={{ marginRight: "5px" }} />
                Cancel
              </Button>

              {activeStep > 0 && (
                <Button
                  className={`${classes.btn} ${classes.btnCancel}`}
                  variant="outlined"
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  Previous
                </Button>
              )}

              {activeStep < 3 ? (
                <Button
                  className={`${classes.btn} ${classes.btnSubmit}`}
                  variant="contained"
                  onClick={nextStep}
                  disabled={isSubmitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  className={`${classes.btn} ${classes.btnSubmit}`}
                  variant="contained"
                  onClick={submitHandler}
                  disabled={isSubmitting || !!reviewError}
                >
                  {isSubmitting
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                    ? "Update"
                    : "Save"}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AzureUserFormPage;